import { applyOfferPercentage } from '@/lib/offers';
import { formatMoney } from '@/lib/format';
import ProductImage from '@/components/feature/ProductImage';
import type { Offer, Product } from '@/lib/types';

interface ProductCardProps {
  product: Product;
  currencySymbol: string;
  offer?: Offer | null;
  onSelect: (product: Product) => void;
  className?: string;
  imageClassName?: string;
  /** Carga prioritaria (imágenes visibles de inmediato). */
  priority?: boolean;
}

export default function ProductCard({
  product,
  currencySymbol,
  offer,
  onSelect,
  className = '',
  imageClassName = 'w-full h-40 bg-background-100 overflow-hidden',
  priority = false,
}: ProductCardProps) {
  const available = product.stock > 0;
  const offerPrice = offer
    ? applyOfferPercentage(Number(product.sale_price), Number(offer.percentage))
    : null;

  return (
    <button
      type="button"
      onClick={() => onSelect(product)}
      className={`text-left rounded-lg border border-background-200 bg-background-50 overflow-hidden flex flex-col hover:border-primary-300 hover:bg-background-100/50 transition cursor-pointer ${className}`}
    >
      <div className={imageClassName}>
        <ProductImage
          url={product.image_url}
          name={product.name}
          className="w-full h-full object-cover object-top"
          placeholderClassName="w-full h-full"
          iconClassName="text-3xl"
          sizes="(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw"
          priority={priority}
        />
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <p className="text-sm font-semibold text-foreground-950 leading-snug line-clamp-2">
          {product.name}
        </p>
        {product.description && (
          <p className="mt-1 text-xs text-foreground-500 line-clamp-2">{product.description}</p>
        )}
        {offer ? (
          <div className="mt-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 text-background-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
              <i className="ri-fire-line" /> OFERTA
            </span>
            <p className="mt-1 text-xs text-foreground-400 line-through">
              {formatMoney(Number(product.sale_price), currencySymbol)}
            </p>
            <p className="text-base font-heading font-bold text-primary-700">
              {formatMoney(offerPrice ?? 0, currencySymbol)}
            </p>
            <p className="text-xs font-medium text-accent-700">
              Oferta -{Number(offer.percentage)}%
            </p>
          </div>
        ) : (
          <p className="mt-2 text-base font-heading font-bold text-primary-700">
            {formatMoney(Number(product.sale_price), currencySymbol)}
          </p>
        )}
        <span
          className={`mt-1 inline-flex items-center gap-1 text-xs font-medium ${
            available ? 'text-accent-700' : 'text-foreground-400'
          }`}
        >
          <i className={available ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'} />
          {available ? 'Disponible' : 'Agotado'}
        </span>
        <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-foreground-500">
          <i className="ri-eye-line" /> Ver detalles
        </span>
      </div>
    </button>
  );
}